@if (!showLoading()) {
  <div class="overflow-x-auto">
    <table class="table table-xs md:table-sm w-full table-fixed">
      <colgroup>
        <col class="w-13 sm:w-7">
        <col class="w-[80px] md:w-[100px] lg:w-[120px] xl:w-[150px]">
        <col class="w-[100px]">
        <col class="w-auto">
        <col class="w-[50px] md:w-[70px] lg:w-[100px]">
      </colgroup>
      <tbody>
        @if (!stockItems || stockItems.length === 0) {
          <tr>
            <td colspan="5" class="px-3 py-5 bg-base-200/40">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3 text-sm text-base-content/60">
                  <span class="opacity-50">—</span>
                  <div>
                    <div class="font-medium text-base-content/70">No items in this stock</div>
                    <div class="text-xs text-base-content/50">
                      Use “Add” above or drag an item here from another stock.
                    </div>
                  </div>
                </div>
                <div class="hidden sm:flex items-center gap-2 text-xs text-base-content/40">
                  <span class="kbd kbd-xs">drag</span>
                  <span>to move</span>
                </div>
              </div>
            </td>
          </tr>
        } @else {
          @for (item of stockItems; track item) {
            <tr
              class="group cursor-pointer hover:bg-primary/10 active:bg-primary/20 transition-colors"
              [attr.draggable]="!isTouchDevice ? 'true' : null"
              (dragstart)="!isTouchDevice ? startDrag($event, item) : null"
              (dragend)="!isTouchDevice ? endDrag($event) : null"
              [class.editing]="isEditable(item.id)"
              (click)="onRowClick(item, $event)">

              <!-- Drag Handle -->
              <td class="align-middle drag-handle-cell">
                @if (!isTouchDevice) {
                  <span class="drag-handle-icon opacity-40 hover:opacity-100">⋮⋮</span>
                }
                @if (isTouchDevice) {
                  <ui-button
                    draggable="true"
                    size="sm"
                    (dragstart)="startDrag($event, item)"
                    (dragend)="endDrag($event)"
                    (click)="$event.stopPropagation()">
                    ⋮⋮
                  </ui-button>
                }
              </td>

              <!-- Created Date -->
              <td class="align-middle" [hidden]="isEditable(item.id)">
                <small class="font-bold">{{ item.created_at | date:'d.MMM yy' }}</small>
                <br/>
                <small class="text-base-content/60">
                  <fa-icon [icon]="faClock" class="mr-1"></fa-icon>
                  {{ item.created_at | date:'HH:mm' }}
                  <!-- Temperature zone -->
                  <fa-icon
                    [icon]="zoneIcon(item)"
                    [class]="zoneColorClass(item)"
                    [uiTooltip]="zoneLabel(item)"
                    class="ml-1"></fa-icon>
                </small>
              </td>

              <!-- Expiry Date -->
              <td class="align-middle" [hidden]="isEditable(item.id)">
                @if (item.expiry_date) {
                  <div class="cursor-pointer"
                       (click)="openExpiryEdit(item.id, item.expiry_date); $event.stopPropagation()">
                    <small>
                    <span
                      class="imprecise-expiry-prefix"
                      [attr.title]="isImpreciseExpiry(item) ? impreciseExpiryLabel(item) : null"
                      aria-hidden="true">
                      {{ isImpreciseExpiry(item) ? '~' : '' }}
                    </span>
                      <span [class.imprecise-expiry-text]="isImpreciseExpiry(item)">
                      {{ item.expiry_date | date:'d. MMM yy' }}
                    </span>
                    </small>
                  </div>
                }
                @if (!item.expiry_date) {
                  <div>
                    <ui-button
                      variant="ghost"
                      [appearance]="'icon'"
                      [tone]="'transparent'"
                      size="sm"
                      (click)="openExpiryEdit(item.id); $event.stopPropagation()"
                      [hidden]="expiryEditStates.get(item.id)">
                      <fa-icon [icon]="faCalendarAlt"></fa-icon>
                    </ui-button>
                  </div>
                }
              </td>

              <!-- Expiry Edit Mode -->
              <td colspan="4" [hidden]="!isEditable(item.id)" (click)="$event.stopPropagation()">
                <div class="flex gap-2 items-center" [hidden]="!isEditable(item.id)" (click)="$event.stopPropagation()">
                  <ui-datepicker
                    class="flex-1"
                    [value]="expiryEdits.get(item.id) || item.expiry_date"
                    [autoOpen]="isEditable(item.id)"
                    placeholder="Select expiry date"
                    size="sm"
                    (dateSelected)="onDateSelected(item.id, $event)"
                    (closed)="cancelExpiryEdit(item.id)">
                  </ui-datepicker>
                </div>
              </td>

              <!-- Usage -->
              <td [hidden]="isEditable(item.id)">
                <div class="flex justify-end">
                  <div class="text-end">

                    @if (isSinglePiece(item)) {

                      <ui-badge
                        [variant]="getQuantityLeft(item) === 1 ? 'success' : 'error'"
                      >
                        @if (getQuantityLeft(item) === 0) {
                          Empty
                        } @else {
                          Full
                        }
                      </ui-badge>

                    } @else {
                      <!-- Usage Text -->
                      <div class="mb-1">
                        @if (getQuantityLeft(item) === 0 && getQuantityFull(item) === 0) {
                          Empty
                        } @else {
                          @if (getQuantityLeft(item) > 0 && getQuantityLeft(item) === getQuantityFull(item)) {
                            @if (quantityType === 'variable') {
                              {{ getQuantityFull(item) | number:'1.0-2' }} {{ item.unit }}
                            } @else {
                              Full
                            }
                          } @else {
                            {{ getQuantityLeft(item) | number:'1.0-2' }}
                            /
                            {{ getQuantityFull(item) | number:'1.0-2' }}
                            @if (!isPiece(item.unit_type)) {
                              {{ item.unit }}
                            }
                          }
                        }
                      </div>
                    }

                    <!-- Progress bar for weight/volume -->
                    @if (isWeightOrVolume(item.unit_type)) {
                      <div class="w-[75px] md:w-[120px] xl:w-[200px]">
                        <ui-progress
                          [value]="getUsagePercentage(item)"
                          [max]="100"
                          [color]="getUsagePercentage(item) == 100 ? 'success' : getUsagePercentage(item) > 0 ? 'warning' : 'error'"
                          size="sm">
                        </ui-progress>
                      </div>
                    } @else {
                      <!-- Dots for pieces -->
                      @if (!isSinglePiece(item)) {
                        <div class="dots inline-flex gap-0.5" [ngClass]="getDotVariantClass(item)">
                          @for (dot of getDots(item); track dot; let i = $index) {
                            <span
                              class="dot"
                              [class.dot-filled]="i < getFilledDots(item)"
                              [class.dot-empty]="i >= getFilledDots(item)">
  </span>
                          }
                        </div>
                      }
                    }
                  </div>
                </div>
              </td>

              <!-- Actions -->
              <td
                class="align-middle text-right group/actions overflow-hidden pr-1 md:pr-3"
                [hidden]="isEditable(item.id)">
                <div class="relative h-9 w-full min-w-0">

                  <!-- Delete (default visible; hide on row hover; BUT show again when hovering action cell) -->
                  <ui-button
                    variant="ghost"
                    size="sm"
                    appearance="icon"
                    tone="transparent"
                    className="text-error absolute inset-y-0 right-0
                 opacity-100 pointer-events-auto transition-opacity
                 group-hover:opacity-0
                 group-hover/actions:opacity-100"
                    (clicked)="removeItemByIdFromStock(item.id); $event.stopPropagation()"
                  >
                    <fa-icon [icon]="faTrash"></fa-icon>
                  </ui-button>

                  <!-- Edit (hidden by default; show on row hover; BUT hide when hovering action cell) -->
                  <ui-button
                    variant="ghost"
                    size="sm"
                    appearance="icon"
                    tone="transparent"
                    className="absolute inset-y-0 right-0
                 opacity-0 pointer-events-none transition-opacity
                 group-hover:opacity-100 group-hover:pointer-events-auto
                 group-hover/actions:opacity-0 group-hover/actions:pointer-events-none"
                    (clicked)="onUsageEditClicked(item); $event.stopPropagation()"
                  >
                    <fa-icon [icon]="faEdit"></fa-icon>
                  </ui-button>

                </div>
              </td>


            </tr>
          }
        }
      </tbody>
    </table>
  </div>
} @else {
  <div class="flex justify-around items-center gap-4">
    <strong>Loading...</strong>
    <span class="loading loading-spinner loading-md"></span>
  </div>
}

<!-- Stock Item Modal -->
<app-stock-item></app-stock-item>
